import type { Ref } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import type { Timeout } from './types';
import useAsRef from '../useAsRef';

export type TimedCallback = {
  timeoutRef: Ref<Timeout | null>,
  start: () => boolean,
  stop: () => boolean,
};

// TODO: Consider updating the timeout if the time parameter changes.
type UseTimedCallback = (f: () => void, time: number, milliseconds?: boolean) => TimedCallback;
const useTimedCallback: UseTimedCallback = (f, time, milliseconds = false) => {
  /* The `useAsRef` is necessary to allow the `start` function to call the latest version of `f`.
   * Without this, any changes to `f` after `start` is called will not be reflected
   * when the timeout completes. */
  const fRef = useAsRef(f);
  const timeoutRef = useRef<Timeout | null>(null);

  // If the hook is unloaded, cancel the timeout automatically.
  useEffect(() => () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  const start = useCallback(() => {
    if (!timeoutRef.current) {
      const timeMS = milliseconds ? time : time * 1000;
      timeoutRef.current = setTimeout(() => { fRef.current(); }, timeMS);
      return true;
    }
    return false;
  }, [fRef, time, milliseconds]);

  const stop = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
      return true;
    }
    return false;
  }, []);

  return { start, stop, timeoutRef };
};

export default useTimedCallback;
